import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useDropdownPosition } from '../hooks/useDropdownPosition';
import { useClickOutside } from '../hooks/useClickOutside';
import '../styles/components/EllipsisOptions.scss';

export interface OptionItem {
    icon: IconDefinition;
    label: string;
    onClick: () => void;
    variant?: 'default' | 'danger' | 'primary';
}

interface EllipsisOptionsProps {
    options: OptionItem[];
    size?: 'default' | 'large';
}

const EllipsisOptions: React.FC<EllipsisOptionsProps> = ({
    options,
    size = 'default',
}) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const triggerRef = useRef<HTMLDivElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);

    // Use custom hooks for positioning and outside clicks
    const dropdownPosition = useDropdownPosition({
        isOpen: isModalVisible,
        triggerRef,
        alignment: 'right',
        offsetY: 4,
        minWidth: size === 'large' ? 220 : 180,
    });

    useClickOutside({
        refs: [triggerRef, modalRef],
        isOpen: isModalVisible,
        onClickOutside: () => setIsModalVisible(false),
    });

    const toggleModal = (event: React.MouseEvent) => {
        event.stopPropagation();
        setIsModalVisible(!isModalVisible);
    };

    // Render modal using portal
    const renderModal = () => {
        if (!isModalVisible) return null;

        const modal = (
            <div
                className={`ellipsis-options-modal size-${size}`}
                ref={modalRef}
                style={{
                    position: 'absolute',
                    top: `${dropdownPosition.top}px`,
                    left: `${dropdownPosition.left}px`,
                    width: `${dropdownPosition.width}px`,
                }}
            >
                <div className="options-modal-content">
                    {options.map((option, index) => (
                        <React.Fragment key={index}>
                            <button
                                className={`modal-button ${option.variant || ''}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    option.onClick();
                                    setIsModalVisible(false);
                                }}
                            >
                                <FontAwesomeIcon icon={option.icon} />{' '}
                                {option.label}
                            </button>
                            {index < options.length - 1 && <hr />}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        );

        return ReactDOM.createPortal(modal, document.body);
    };

    return (
        <div
            className={`ellipsis-options-container size-${size}`}
            ref={triggerRef}
        >
            <button
                className={`ellipsis-button ${isModalVisible ? 'active' : ''}`}
                onClick={toggleModal}
            >
                <FontAwesomeIcon icon={faEllipsisVertical} />
            </button>

            {renderModal()}
        </div>
    );
};

export default EllipsisOptions;
